/**
 * All-countries AI results — country tabs + per-drop breakdown with list + group labels.
 */

import { useState } from 'react';
import { Languages, MessageSquare, User } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { useTextPromptGroups } from '@/features/campaign-texts/hooks/useTextPromptGroups';
import { cn } from '@/lib/utils';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import { countryGenResultFullyTranslated, countryGenResultHasTexts } from '../shared/translateBulkEditCountryTexts';
import type { TextPickBatches } from '../shared/bulkEditAiTextPicks';

import { buildCountryResultsTextPickInteraction } from './buildCountryResultsTextPickInteraction';
import { CountBadge, CountryResultContent } from './result';

import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import type { CountryGenResult } from '../shared/multiCountryBulkEdit.types';

interface Props {
  countryKeys: string[];
  resultsByCountry: Record<string, CountryGenResult>;
  dropLabelsByCountry?: Record<string, DropLabel[]>;
  onTranslateCountry?: (countryKey: string) => void | Promise<void>;
  translatingCountryKey?: string | null;
  enableTextPicks?: boolean;
  textPicks?: Record<string, TextPickBatches>;
  toggleAiTextPick?: (countryKey: string, batchIdx: number, lineIdx: number) => void;
  setAllAiTextPicksForBatch?: (countryKey: string, batchIdx: number, value: boolean) => void;
  regenerateUnselectedInBatch?: (countryKey: string, batchIdx: number) => void | Promise<void>;
  regenTarget?: { ck: string; batchIdx: number } | null;
  isRegeneratingTexts?: boolean;
  disableTextPickInteractions?: boolean;
  translateMissingInBatch?: (countryKey: string, batchIdx: number) => void | Promise<void>;
  translatingBatch?: { ck: string; batchIdx: number } | null;
}

export function CountryResultsPanel({
  countryKeys,
  resultsByCountry,
  dropLabelsByCountry,
  onTranslateCountry,
  translatingCountryKey,
  enableTextPicks,
  textPicks,
  toggleAiTextPick,
  setAllAiTextPicksForBatch,
  regenerateUnselectedInBatch,
  regenTarget,
  isRegeneratingTexts,
  disableTextPickInteractions,
  translateMissingInBatch,
  translatingBatch,
}: Props) {
  const { data: promptGroups } = useTextPromptGroups();
  const withResults = countryKeys.filter((ck) => resultsByCountry[ck]);
  const [activeTab, setActiveTab] = useState(withResults[0] ?? '');
  if (withResults.length === 0) return null;
  const active = withResults.includes(activeTab) ? activeTab : withResults[0]!;
  const activeResult = resultsByCountry[active];
  const showTranslateBtn =
    Boolean(onTranslateCountry) &&
    activeResult &&
    countryGenResultHasTexts(activeResult) &&
    !countryGenResultFullyTranslated(activeResult);
  const isTranslating = translatingCountryKey === active || translatingBatch?.ck === active;

  return (
    <div className="overflow-hidden rounded-lg bg-muted/20 ring-1 ring-border/35">
      <div className="flex flex-col gap-2 border-b border-border/30 bg-muted/10 px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-2.5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Results
          </span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {withResults.map((ck) => {
              const r = resultsByCountry[ck]!;
              return (
                <button
                  key={ck}
                  type="button"
                  onClick={() => setActiveTab(ck)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-medium transition-colors',
                    active === ck
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/45'
                      : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                  )}
                >
                  <span>{countryToFlagEmoji(ck)}</span>
                  <span>{ck}</span>
                  {r.texts.length > 0 && (
                    <CountBadge icon={<MessageSquare className="h-2.5 w-2.5" />} count={r.texts.length} color="blue" />
                  )}
                  {r.senders.length > 0 && (
                    <CountBadge icon={<User className="h-2.5 w-2.5" />} count={r.senders.length} color="green" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {showTranslateBtn && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 gap-1.5 self-start text-[10px] sm:self-auto"
            disabled={isTranslating}
            onClick={() => void onTranslateCountry?.(active)}
          >
            {isTranslating ? <LoadingSpinner className="h-3 w-3" /> : <Languages className="h-3 w-3" />}
            Translate to English
          </Button>
        )}
      </div>
      <div className="min-h-0 min-w-0 bg-card/30 px-2.5 py-3 sm:px-3">
        {resultsByCountry[active] && (
          <CountryResultContent
            result={resultsByCountry[active]!}
            dropLabels={dropLabelsByCountry?.[active]}
            promptGroups={promptGroups}
            textPickInteraction={buildCountryResultsTextPickInteraction({
              active,
              activeResult: activeResult!,
              enableTextPicks,
              textPicks,
              toggleAiTextPick,
              setAllAiTextPicksForBatch,
              regenerateUnselectedInBatch,
              regenTarget,
              isRegeneratingTexts,
              disableTextPickInteractions,
              onTranslateCountry,
              translateMissingInBatch,
              translatingCountryKey,
              translatingBatch,
            })}
          />
        )}
      </div>
    </div>
  );
}
